/**
 * useAnalyticsData Hook
 *
 * React hook for fetching and managing analytics dashboard data
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { AnalyticsFilters, UseAnalyticsDataResult, DashboardData, AnalyticsAggregate, MetricType } from "../types/analytics";
import {
	fetchAnalyticsAggregates,
	processAggregatesIntoDashboard,
	createDefaultDateRange,
	validateDateRange,
} from "../services/analytics/analyticsDataService";

/**
 * Hook for managing analytics data with filters and state
 */
export function useAnalyticsData(initialFilters: AnalyticsFilters = {}): UseAnalyticsDataResult {
	const [data, setData] = useState<DashboardData | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [filters, setFiltersState] = useState<AnalyticsFilters>({
		dateRange: createDefaultDateRange(),
		limit: 100,
		...initialFilters,
	});

	// Validate filters
	const validatedFilters = useMemo(() => {
		const validated = { ...filters };

		// Ensure we have a valid date range
		if (!validated.dateRange || !validateDateRange(validated.dateRange)) {
			validated.dateRange = createDefaultDateRange();
		}

		return validated;
	}, [filters]);

	/**
	 * Fetch analytics data based on current filters
	 */
	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const allAggregates: AnalyticsAggregate[] = [];
			let nextToken: string | undefined;
			let hasMore = true;

			// Fetch all pages of data
			while (hasMore) {
				const result = await fetchAnalyticsAggregates({
					...validatedFilters,
					nextToken,
				});

				allAggregates.push(...result.aggregates);
				nextToken = result.nextToken;
				hasMore = result.hasMore;

				// Safety limit to prevent infinite loops
				if (allAggregates.length > 10000) {
					console.warn("Analytics data fetch exceeded 10,000 records, stopping");
					break;
				}
			}
			// Process the aggregates into dashboard data
			const dashboardData = processAggregatesIntoDashboard(allAggregates, validatedFilters.dateRange!);

			setData(dashboardData);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "Failed to fetch analytics data";
			console.error("Analytics data fetch error:", err);
			setError(errorMessage);
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [validatedFilters]);

	/**
	 * Refresh data (manually trigger reload)
	 */
	const refresh = useCallback(async () => {
		await fetchData();
	}, [fetchData]);

	/**
	 * Update filters and trigger data refetch
	 */
	const setFilters = useCallback((newFilters: AnalyticsFilters) => {
		setFiltersState((prev) => ({
			...prev,
			...newFilters,
		}));
	}, []);

	// Fetch data when filters change
	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return {
		data,
		loading,
		error,
		refresh,
		setFilters,
		filters: validatedFilters,
	};
}

/**
 * Hook for fetching analytics data for specific users
 */
export function useUserAnalyticsData(userIds: string[]) {
	const baseFilters = useMemo(
		() => ({
			metricType: "user-hourly" as MetricType,
			dateRange: createDefaultDateRange(),
		}),
		[],
	);

	const { data, loading, error, refresh, setFilters } = useAnalyticsData(baseFilters);

	// Filter user metrics to only include specified users
	const filteredData = useMemo(() => {
		if (!data || userIds.length === 0) return data;

		return {
			...data,
			userMetrics: data.userMetrics.filter((metric) => userIds.includes(metric.userId)),
		};
	}, [data, userIds]);

	return {
		data: filteredData,
		loading,
		error,
		refresh,
		setFilters,
	};
}

/**
 * Hook for fetching analytics data for specific groups
 */
export function useGroupAnalyticsData(groupIds: string[]) {
	const baseFilters = useMemo(
		() => ({
			metricType: "group-hourly" as MetricType,
			dateRange: createDefaultDateRange(),
		}),
		[],
	);

	const { data, loading, error, refresh, setFilters } = useAnalyticsData(baseFilters);

	// Filter group metrics to only include specified groups
	const filteredData = useMemo(() => {
		if (!data || groupIds.length === 0) return data;

		return {
			...data,
			groupMetrics: data.groupMetrics.filter((metric) => groupIds.includes(metric.groupId)),
		};
	}, [data, groupIds]);

	return {
		data: filteredData,
		loading,
		error,
		refresh,
		setFilters,
	};
}

/**
 * Hook for system-wide analytics (no filtering by user/group)
 */
export function useSystemAnalyticsData() {
	const baseFilters = useMemo(
		() => ({
			metricType: "system-hourly" as MetricType,
			dateRange: createDefaultDateRange(),
		}),
		[],
	);

	return useAnalyticsData(baseFilters);
}

/**
 * Hook for real-time analytics summary (key metrics only)
 */
export function useAnalyticsSummary() {
	const { data, loading, error, refresh } = useAnalyticsData({
		metricType: "system-hourly" as MetricType,
		dateRange: createDefaultDateRange(),
		limit: 50, // Smaller limit for summary
	});

	const summary = useMemo(() => {
		if (!data) return null;

		return {
			totalUsers: data.summary.totalUsers,
			totalSessions: data.summary.totalSessions,
			totalEvents: data.summary.totalEvents,
			totalExports: data.summary.totalExports,
			averageSessionDuration: data.summary.averageSessionDuration,
			topEventTypes: data.timeSeriesData.eventTypeBreakdown.slice(0, 5),
			recentActivity: data.timeSeriesData.hourlyActivity.slice(-24), // Last 24 hours
		};
	}, [data]);

	return {
		summary,
		loading,
		error,
		refresh,
	};
}
